import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    ViewEncapsulation,
    forwardRef,
    inject,
    Injector,
    signal,
    HostBinding,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    FormControlName,
    FormGroupDirective,
    FormsModule,
    NG_VALUE_ACCESSOR,
    NgControl,
    ReactiveFormsModule,
} from '@angular/forms';
import { ClickOutsideDirective } from '@shared/click-outside/click-outside.directive';

import { countries } from '@company-id-shared/constants/constants';
import { Country } from '@company-id-shared/types/country.type';
import { ChevronComponent } from '@shared/chevron/chevron.component';
import { CountriesTranslatePipe } from '@content/contact-us/connect-with-our-team/countries-dropdown/countries-translate.pipe';

@Component({
    selector: 'companyid-countries-dropdown',
    imports: [ReactiveFormsModule, ClickOutsideDirective, FormsModule, ChevronComponent, CountriesTranslatePipe],
    templateUrl: './countries-dropdown.component.html',
    styleUrls: ['./countries-dropdown.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CountriesDropdownComponent),
            multi: true,
        },
    ],
})
export class CountriesDropdownComponent implements ControlValueAccessor, OnInit {
    public filteredCountries = signal<Country[]>(countries);
    public flag = signal<string>('');
    public showDropdown = signal<boolean>(false);

    private readonly countries: Country[] = countries;
    private readonly fb = inject(FormBuilder);
    private readonly injector = inject(Injector);

    public control: FormControl<string | null> = this.fb.control('');
    public countryControl = new FormControl('');

    @HostBinding('class')
    public readonly hostClassname = 'flex flex-col relative gap-y-2.5 w-[135px] z-20';

    public writeValue(code: string): void {
        if (!code) {
            return;
        }
        const country = this.findCountryByCode(code);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        country && this.flag.set(country.flag);
    }

    public registerOnChange(fn: () => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public onChange: (value: string | boolean | null) => void = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public onTouched: () => void = () => {};

    public ngOnInit(): void {
        const ngControl = this.injector.get(NgControl);
        // TODO(andriikamaldinov1): think about change default value from parent
        if (ngControl instanceof FormControlName) {
            this.control = this.injector.get(FormGroupDirective).getControl(ngControl);
            if (this.control.value) {
                const country = this.findCountryByCode(this.control.value);
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                country && this.flag.set(country.flag);
            }
        }

        this.countryControl.valueChanges.subscribe((value) => {
            if (!value) {
                this.filteredCountries.update(() => this.countries);
                return;
            }
            this.filteredCountries.update(() =>
                this.countries.filter(
                    (country) =>
                        country.dial_code.includes(value) || country.name.toLowerCase().includes(value.toLowerCase()),
                ),
            );
        });
    }

    public selectCountry(country: Country): void {
        this.control.setValue(country.dial_code);
        this.flag.set(country.flag);
        this.countryControl.setValue('');
        this.showDropdown.set(false);
        this.onChange(country.dial_code);
    }

    public onKeydown(event: KeyboardEvent, country: Country): void {
        if (event.key === 'Enter') {
            this.selectCountry(country);
        }
    }

    public toggleDropdown(isOpen: boolean): void {
        this.showDropdown.set(isOpen);
    }

    private findCountryByCode(code: string | null): Country | undefined {
        return this.countries.find(({ dial_code }) => dial_code === (code || '+380'));
    }
}
